import { useEffect, useState, useCallback } from "react";
import { useAuth as useClerkAuth, useUser, useClerk } from "@clerk/clerk-react";
import { AuthContext } from "@/context/AuthContext";
import api, { setClerkTokenGetter } from "@/lib/api";

/**
 * Auth bridge used when Clerk is configured. Derives the session from Clerk,
 * carries a fresh Clerk token on every API call, and syncs to the backend
 * (/auth/me) which verifies the token and provisions/links the MongoDB user
 * (preserving roles). Exposes the SAME context shape as the legacy provider so
 * the rest of the app is unchanged.
 */
export function ClerkAuthProvider({ children }) {
  const { isLoaded, isSignedIn, getToken } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const clerk = useClerk();
  const [user, setUser] = useState(undefined); // undefined=loading, null=guest, obj=auth

  useEffect(() => {
    setClerkTokenGetter(() => getToken());
    return () => setClerkTokenGetter(null);
  }, [getToken]);

  const sync = useCallback(async () => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    }
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    sync();
  }, [sync, clerkUser?.id]);

  const logout = async () => {
    try {
      await clerk.signOut();
    } catch (e) {
      console.warn("[Auth] Clerk sign-out failed (clearing session anyway)", e);
    }
    setClerkTokenGetter(null);
    setUser(null);
  };

  // Email/password login is not used with Clerk; sign-in is via <SignIn>.
  const login = async () => ({ ok: false, error: "Please sign in with Google." });

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh: sync, clerk: true }}>
      {children}
    </AuthContext.Provider>
  );
}
